/* eslint-disable max-lines */
import { ChangeDetectorRef, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { AbstractControl, ValidationErrors } from '@angular/forms';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { NUMBER_WITH_SEPARATORS_PATTERN } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import type {
  InterviewResult,
  InterviewResultAnswer,
  InterviewResultAnswerOption,
  RatingLevelType,
} from '@core/interview-results-types';
import type { InterviewQuestion, InterviewTemplateDetail } from '@core/interview-templates-types';
import { QuestionKind } from '@core/interview-templates-types';
import type { EditorJSONState } from '@core/prosemirror-types';
import { CurrencySelectors } from '@recruitee/currency-data';
import type {
  EvaluationAnswerForm,
  EvaluationChoiceOption,
  SkillRatingForm,
  TemplateEvaluationForm,
  TemplateEvaluationFormValue,
} from '@recruitee/evaluations-types';

import { EvaluationFormSubmissionService } from '../evaluation-form-submission.service';

const atLeastOneSelected = (control: AbstractControl): ValidationErrors | null =>
  (<FormArray<FormControl<boolean>>>control).controls.some(item => item.value)
    ? null
    : { required: true };

const atLeastOneAttachment = (control: AbstractControl): ValidationErrors | null =>
  (<FormArray<FormControl<number>>>control).length > 0 ? null : { required: true };

@Injectable()
export class TemplateEvaluationFormService {
  private readonly fb = inject(FormBuilder);
  private readonly formSubmissionService = inject(EvaluationFormSubmissionService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly currenciesMap = toSignal(this.store.select(CurrencySelectors.currenciesMap), {
    initialValue: <Record<string, string>>{},
  });
  private readonly useProsemirror = injectDevFlag(DevFlag.ProsemirrorInEvaluations);

  public readonly form: TemplateEvaluationForm = this.fb.group({
    score: new FormControl<number | null>(null),
    ratingNote: new FormControl<string | null>(null),
    ratingNoteJson: new FormControl<EditorJSONState | null>(null),
    answers: new FormArray<EvaluationAnswerForm>([]),
  });

  public currencyLabel(code: string | null): string {
    if (!code) return '';
    const name = this.currenciesMap()[code];
    return name ? `${name} (${code})` : code;
  }

  public applyTemplate(template: InterviewTemplateDetail): void {
    this.setRatingRequired(!!template.ratingRequired);
    this.buildAnswers(template.interviewQuestions ?? []);
  }

  public applyResult(result: InterviewResult): void {
    const answers = result.interviewResultAnswers ?? [];

    this.setRatingRequired(!!result.ratingRequired);
    this.buildAnswers(answers.map(answer => this.answerToQuestion(answer)));
    this.patchFromResult(result);

    this.form.controls.score.setValue(result.score ?? null);
    this.form.controls.ratingNote.setValue(result.ratingNote ?? null);
    this.form.controls.ratingNoteJson.setValue(result.ratingNoteJson ?? null);

    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  public restoreDraft(value: TemplateEvaluationFormValue): void {
    this.form.controls.score.setValue(value.score);
    this.form.controls.ratingNote.setValue(value.ratingNote);
    this.form.controls.ratingNoteJson.setValue(value.ratingNoteJson);

    const savedById = new Map(value.answers.map(answer => [answer.id, answer]));

    this.form.controls.answers.controls.forEach(answer => {
      const saved = savedById.get(answer.controls.id.value);
      if (!saved) return;

      answer.controls.content.setValue(saved.content);
      answer.controls.contentJson.setValue(saved.contentJson);
      answer.controls.note.setValue(saved.note);
      answer.controls.noteJson.setValue(saved.noteJson);
      saved.contents.forEach((content, index) =>
        answer.controls.contents.at(index)?.setValue(content),
      );
      saved.selections.forEach((selection, index) =>
        answer.controls.selections.at(index)?.setValue(selection),
      );

      answer.controls.attachmentIds.clear();
      saved.attachmentIds.forEach(id =>
        answer.controls.attachmentIds.push(new FormControl(id, { nonNullable: true })),
      );

      saved.skillRatings.forEach(savedSkill => {
        const skill = answer.controls.skillRatings.controls.find(
          item => item.controls.skillId.value === savedSkill.skillId,
        );
        skill?.controls.proficiencyId.setValue(savedSkill.proficiencyId);
      });
    });

    this.form.markAsDirty();
    this.form.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  public clear(): void {
    this.form.controls.score.reset(null);
    this.form.controls.ratingNote.reset(null);
    this.form.controls.ratingNoteJson.reset(null);
    this.form.controls.answers.controls.forEach(answer => {
      answer.controls.content.reset(null);
      answer.controls.contentJson.reset(null);
      answer.controls.note.reset(null);
      answer.controls.noteJson.reset(null);
      answer.controls.contents.controls.forEach(content => content.reset(null));
      answer.controls.selections.controls.forEach(selection => selection.reset(false));
      answer.controls.attachmentIds.clear();
      answer.controls.skillRatings.controls.forEach(skill =>
        skill.controls.proficiencyId.reset(null),
      );
    });
    this.form.markAsUntouched();
    this.formSubmissionService.reset();
    this.form.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  public reset(): void {
    this.form.reset();
    this.form.controls.answers.clear();
  }

  public getAnswersPayload(): InterviewResultAnswer[] {
    return this.form
      .getRawValue()
      .answers.map(
        ({
          selections,
          skillRatings,
          currency,
          content,
          contentJson,
          note,
          noteJson,
          ...answer
        }) => {
          const isProsemirrorText =
            answer.questionKind === QuestionKind.Text && this.useProsemirror;

          return {
            ...answer,
            ...(isProsemirrorText ? { contentJson } : { content: content ?? undefined }),
            ...(this.useProsemirror ? { noteJson } : {}),
            questionOptions: currency ? { currency } : undefined,
            contents: <string[]>(
              (answer.questionKind === QuestionKind.MultipleChoice
                ? selections.map((checked, index) => (checked ? answer.options[index].title : null))
                : answer.contents)
            ),
            skillRatings: skillRatings.map(({ skillId, proficiencyId }) => ({
              skillId,
              proficiencyId,
            })),
          };
        },
      );
  }

  private answerToQuestion(answer: InterviewResultAnswer): InterviewQuestion {
    return <InterviewQuestion>{
      id: answer.id,
      title: answer.questionTitle,
      hint: answer.questionHint,
      kind: answer.questionKind,
      required: answer.questionRequired,
      interviewQuestionOptions: answer.options,
      options: answer.questionOptions,
      skillsData: answer.questionOptions,
    };
  }

  private patchFromResult(result: InterviewResult): void {
    const answersById = new Map(result.interviewResultAnswers.map(answer => [answer.id, answer]));

    this.form.controls.answers.controls.forEach(answer => {
      const saved = answersById.get(answer.controls.id.value);
      if (!saved) return;

      answer.controls.note.setValue(saved.note ?? null);
      answer.controls.noteJson.setValue(saved.noteJson ?? null);

      switch (answer.controls.questionKind.value) {
        case QuestionKind.Scorecard:
          answer.controls.contents.controls.forEach((content, index) =>
            content.setValue(<RatingLevelType | null>(saved.contents?.[index] ?? null)),
          );
          break;
        case QuestionKind.MultipleChoice:
          answer.controls.selections.controls.forEach((selection, index) =>
            selection.setValue(!!saved.contents?.[index]),
          );
          break;
        case QuestionKind.File:
          (saved.attachmentIds ?? []).forEach(id =>
            answer.controls.attachmentIds.push(new FormControl(id, { nonNullable: true })),
          );
          break;
        case QuestionKind.Skills:
          answer.controls.skillRatings.controls.forEach(skill => {
            const match = saved.skillRatings?.find(
              item => item.skillId === skill.controls.skillId.value,
            );
            skill.controls.proficiencyId.setValue(match?.proficiencyId ?? null);
          });
          break;
        default:
          answer.controls.content.setValue(saved.content ?? null);
          answer.controls.contentJson.setValue(saved.contentJson ?? null);
      }
    });
  }

  private setRatingRequired(required: boolean): void {
    const score = this.form.controls.score;
    score.setValidators(required ? Validators.required : null);
    score.updateValueAndValidity();
  }

  private buildAnswers(questions: InterviewQuestion[]): void {
    const answers = this.form.controls.answers;
    answers.clear();

    questions
      .filter(
        question => this.isSupportedKind(question.kind) && this.hasRenderableContent(question),
      )
      .forEach(question => answers.push(this.createAnswer(question)));
  }

  private hasRenderableContent(question: InterviewQuestion): boolean {
    return question.kind !== QuestionKind.Skills || !!question.skillsData?.skills?.length;
  }

  private createAnswer(question: InterviewQuestion): EvaluationAnswerForm {
    const required = !!question.required;
    const isProsemirrorText = question.kind === QuestionKind.Text && this.useProsemirror;

    return this.fb.group({
      id: new FormControl(question.id ?? 0, { nonNullable: true }),
      questionTitle: new FormControl<string | null>(question.title ?? null),
      questionHint: new FormControl(typeof question.hint === 'string' ? question.hint : '', {
        nonNullable: true,
      }),
      questionKind: new FormControl(question.kind ?? QuestionKind.String, { nonNullable: true }),
      questionRequired: new FormControl(required, { nonNullable: true }),
      content: new FormControl<string | null>(null, [
        ...(this.isContentKind(question.kind) && required && !isProsemirrorText
          ? [Validators.required]
          : []),
        ...(question.kind === QuestionKind.Salary
          ? [Validators.pattern(NUMBER_WITH_SEPARATORS_PATTERN)]
          : []),
      ]),
      contentJson: new FormControl<EditorJSONState | null>(
        null,
        isProsemirrorText && required ? [Validators.required] : [],
      ),
      note: new FormControl<string | null>(null),
      noteJson: new FormControl<EditorJSONState | null>(null),
      currency: new FormControl<string | null>(question.options?.currency ?? null),
      contents: this.createContents(question, required),
      selections: this.createSelections(question, required),
      attachmentIds: this.createAttachmentIds(question, required),
      skillRatings: this.createSkillRatings(question, required),
      options: this.createOptions(question),
    });
  }

  private createSkillRatings(
    question: InterviewQuestion,
    required: boolean,
  ): FormArray<SkillRatingForm> {
    const skills = question.kind === QuestionKind.Skills ? (question.skillsData?.skills ?? []) : [];

    return new FormArray(
      skills.map(
        skill =>
          new FormGroup({
            skillId: new FormControl(skill.id, { nonNullable: true }),
            skillName: new FormControl(skill.name, { nonNullable: true }),
            skillDescription: new FormControl<string | null>(skill.descriptionText),
            proficiencyId: new FormControl<number | null>(
              null,
              required ? Validators.required : null,
            ),
            options: new FormControl<EvaluationChoiceOption[]>(
              skill.proficiencies.map(proficiency => ({
                value: proficiency.id,
                label: proficiency.name,
                description: proficiency.descriptionText,
              })),
              { nonNullable: true },
            ),
          }),
      ),
    );
  }

  private createAttachmentIds(
    question: InterviewQuestion,
    required: boolean,
  ): FormArray<FormControl<number>> {
    return new FormArray<FormControl<number>>(
      [],
      question.kind === QuestionKind.File && required ? atLeastOneAttachment : null,
    );
  }

  private createContents(
    question: InterviewQuestion,
    required: boolean,
  ): FormArray<FormControl<RatingLevelType | null>> {
    const aspects =
      question.kind === QuestionKind.Scorecard ? (question.interviewQuestionOptions ?? []) : [];

    return new FormArray(
      aspects.map(
        () => new FormControl<RatingLevelType | null>(null, required ? Validators.required : null),
      ),
    );
  }

  private createSelections(
    question: InterviewQuestion,
    required: boolean,
  ): FormArray<FormControl<boolean>> {
    const options =
      question.kind === QuestionKind.MultipleChoice
        ? (question.interviewQuestionOptions ?? [])
        : [];

    return new FormArray(
      options.map(() => new FormControl(false, { nonNullable: true })),
      options.length && required ? atLeastOneSelected : null,
    );
  }

  private createOptions(
    question: InterviewQuestion,
  ): FormArray<FormControl<InterviewResultAnswerOption>> {
    return new FormArray(
      this.optionQuestions(question).map(
        option =>
          new FormControl<InterviewResultAnswerOption>(
            { title: option.title ?? '', hint: option.hint ?? '' },
            { nonNullable: true },
          ),
      ),
    );
  }

  private optionQuestions(question: InterviewQuestion) {
    if (!this.hasOptions(question.kind)) return [];

    return question.interviewQuestionOptions ?? [];
  }

  private hasOptions(kind: QuestionKind | null | undefined): boolean {
    return (
      kind === QuestionKind.SingleChoice ||
      kind === QuestionKind.MultipleChoice ||
      kind === QuestionKind.Dropdown ||
      kind === QuestionKind.Scorecard
    );
  }

  private isContentKind(kind: QuestionKind | null | undefined): boolean {
    return (
      kind === QuestionKind.String ||
      kind === QuestionKind.Text ||
      kind === QuestionKind.Boolean ||
      kind === QuestionKind.SingleChoice ||
      kind === QuestionKind.Dropdown ||
      kind === QuestionKind.Date ||
      kind === QuestionKind.Salary
    );
  }

  private isSupportedKind(kind: QuestionKind | null | undefined): boolean {
    return (
      this.isContentKind(kind) ||
      kind === QuestionKind.MultipleChoice ||
      kind === QuestionKind.Scorecard ||
      kind === QuestionKind.File ||
      kind === QuestionKind.Info ||
      kind === QuestionKind.Section ||
      kind === QuestionKind.Skills
    );
  }
}
